/**
 * /admin/lecture-audio — upload page for the user-recorded « apprendre à lire »
 * audio. Lists 39 items, each with an inline file input that uploads via
 * POST /api/lecture/audio/user/upload.
 *
 * The page requires authentication; it does NOT enforce admin role
 * client-side because the backend already gates the upload endpoint.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Upload, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';
import { GlassCard, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Same item set as ApprendreALire.js — kept in sync intentionally.
const ITEMS = [
  // Alphabet
  ['alif', 'أَلِف', 'alif'],     ['baa', 'بَاء', "bāʾ"],     ['taa', 'تَاء', "tāʾ"],
  ['thaa', 'ثَاء', "thāʾ"],     ['jim', 'جِيم', "jīm"],      ['haa1', 'حَاء', "ḥāʾ"],
  ['khaa', 'خَاء', "khāʾ"],     ['dal', 'دَال', "dāl"],      ['dhal', 'ذَال', "dhāl"],
  ['raa', 'رَاء', "rāʾ"],       ['zay', 'زَاي', 'zāy'],      ['sin', 'سِين', "sīn"],
  ['shin', 'شِين', "shīn"],    ['sad', 'صَاد', "ṣād"],      ['dad', 'ضَاد', "ḍād"],
  ['taa2', 'طَاء', "ṭāʾ"],     ['dhaa', 'ظَاء', "ẓāʾ"],     ['ayn', 'عَيْن', "ʿayn"],
  ['ghayn', 'غَيْن', 'ghayn'],  ['faa', 'فَاء', "fāʾ"],      ['qaf', 'قَاف', 'qāf'],
  ['kaf', 'كَاف', 'kāf'],       ['lam', 'لَام', "lām"],      ['mim', 'مِيم', "mīm"],
  ['nun', 'نُون', "nūn"],       ['haa2', 'هَاء', "hāʾ"],     ['waw', 'وَاو', "wāw"],
  ['yaa', 'يَاء', "yāʾ"],
  // Harakāt
  ['haraka_fatha',  'بَ', 'Fatḥa'],
  ['haraka_kasra',  'بِ', 'Kasra'],
  ['haraka_damma',  'بُ', 'Ḍamma'],
  ['haraka_sukun',  'بْ', 'Sukūn'],
  ['haraka_shadda', 'بّ', 'Shadda'],
  // Long vowels
  ['long_alif', 'بَا', 'Madd alif'],
  ['long_yaa',  'بِي', 'Madd yāʾ'],
  ['long_waw',  'بُو', 'Madd wāw'],
  // Tanwīn
  ['tanwin_fath', 'بًا', 'Tanwīn fatḥ'],
  ['tanwin_kasr', 'بٍ',  'Tanwīn kasr'],
  ['tanwin_damm', 'بٌ',  'Tanwīn ḍamm'],
];

const ItemRow = ({ id, glyph, label, hasUserClip, onUploaded, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);   // bust audio cache after upload

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';                             // allow re-upload of same file
    if (!f) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/lecture/audio/user/upload?item_id=${encodeURIComponent(id)}`,
                     fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRefreshKey((k) => k + 1);
      onUploaded?.(id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Échec');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!hasUserClip) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`/lecture/audio/user/${encodeURIComponent(id)}`);
      onDeleted?.(id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Échec');
    } finally {
      setBusy(false);
    }
  };

  const audioSrc =
    `${API_BASE}/api/lecture/audio/user/${encodeURIComponent(id)}.mp3?v=${refreshKey}`;

  return (
    <div
      className="grid grid-cols-[64px_1fr_auto_auto] items-center gap-3 p-3 rounded-lg border border-border bg-surface-elevated/40"
      data-testid={`upload-row-${id}`}
    >
      <div className="font-arabic text-3xl text-primary text-center" dir="rtl">{glyph}</div>
      <div>
        <div className="text-sm text-text-primary">{label}</div>
        <div className="text-[10px] uppercase tracking-wider text-text-muted">{id}</div>
      </div>
      {hasUserClip ? (
        <audio
          controls
          src={audioSrc}
          className="h-8 w-44"
          data-testid={`upload-preview-${id}`}
        />
      ) : (
        <span className="text-xs text-text-muted italic">— pas encore enregistré —</span>
      )}
      <div className="flex items-center gap-2">
        <label
          className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/40 bg-primary/10 text-primary text-xs hover:bg-primary/20"
        >
          {busy ? '…' : <Upload className="w-3.5 h-3.5" />}
          {hasUserClip ? 'Remplacer' : 'Uploader'}
          <input
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm"
            className="hidden"
            onChange={onFile}
            disabled={busy}
            data-testid={`upload-input-${id}`}
          />
        </label>
        {hasUserClip && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={busy}
            className="text-rose-400 hover:text-rose-300"
            data-testid={`upload-delete-${id}`}
            aria-label="Supprimer"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
        {error && (
          <span className="text-xs text-rose-400 inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {error}
          </span>
        )}
      </div>
    </div>
  );
};

export const LectureAudioUpload = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/lecture/audio/user/inventory');
      setInventory(new Set(r.data?.items || []));
      setAuthError(false);
    } catch (err) {
      if (err.response?.status === 401) setAuthError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const progress = useMemo(() => {
    const total = ITEMS.length;
    const done = ITEMS.filter(([id]) => inventory.has(id)).length;
    return { done, total, percent: total ? Math.round((done / total) * 100) : 0 };
  }, [inventory]);

  if (authError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-testid="upload-auth-error">
        <p className="text-text-muted">Connexion requise pour accéder à cette page.</p>
        <Button onClick={() => navigate('/login')} className="mt-4">Se connecter</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="lecture-upload-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours/lecture')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="upload-back"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour à la page Apprendre à lire
      </Button>

      <SectionHeader
        overline="Voix utilisateur"
        title="Enregistrez votre voix pour l'alphabet"
        subtitle="Uploadez un court clip audio (1–2 s) pour chaque lettre, ḥaraka, voyelle longue ou tanwīn. Une fois enregistré, vos clips seront utilisés à la place des voix synthétiques quand l'option « Voix enregistrée » est sélectionnée dans la page Apprendre à lire."
      />

      <div className="mt-6 mb-4 flex items-center gap-3" data-testid="upload-progress">
        <div className="flex-1 h-2 rounded-full bg-surface-elevated overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {progress.done} / {progress.total}
        </Badge>
      </div>

      <GlassCard className="space-y-2">
        {loading
          ? <p className="text-sm text-text-muted text-center py-4">Chargement…</p>
          : ITEMS.map(([id, glyph, label]) => (
              <ItemRow
                key={id}
                id={id}
                glyph={glyph}
                label={label}
                hasUserClip={inventory.has(id)}
                onUploaded={() => setInventory((s) => new Set(s).add(id))}
                onDeleted={() => setInventory((s) => {
                  const n = new Set(s); n.delete(id); return n;
                })}
              />
            ))
        }
      </GlassCard>
    </div>
  );
};

export default LectureAudioUpload;
